import { create } from 'zustand';
import { DashboardKPIs, AnalisisRentabilidad, SistemaAlertas } from '../types';
import { getCurrentEmpresaIdSync } from '../utils/empresaUtils';
import { logger } from '../utils/logger';
import { estadoPagoEsPagado, estadoPagoRequiereCobro } from '../shared/normalizeEstadoPago';

const emptyAnalisis = (): AnalisisRentabilidad => ({
  rentabilidadMensual: [],
  rentabilidadSemanal: [],
  clientesMasRentables: [],
  clientesMenosRentables: [],
  productosMasRentables: [],
  productosMenosRentables: [],
  productosClaseA: [],
  productosClaseB: [],
  productosClaseC: [],
  rentabilidadTotal: 0,
  margenPromedio: 0,
});

const emptyAlertas = (): SistemaAlertas => ({
  alertasInventario: 0,
  alertasChurn: 0,
  alertasRentabilidad: 0,
  alertasPendientes: 0,
  stockBajo: [],
  clientesSinComprarEnDias: [],
});

interface DashboardState {
  kpis: DashboardKPIs | null;
  analisisRentabilidad: AnalisisRentabilidad | null;
  alertas: SistemaAlertas | null;
  isLoading: boolean;
  error: string | null;
  periodoSeleccionado: 'HOY' | 'SEMANA' | 'MES' | 'TRIMESTRE' | 'AÑO';
  
  // Actions
  fetchKPIs: (periodo?: string) => Promise<void>;
  fetchAnalisisRentabilidad: (periodo?: string) => Promise<void>;
  fetchAlertas: () => Promise<void>;
  setPeriodoSeleccionado: (periodo: 'HOY' | 'SEMANA' | 'MES' | 'TRIMESTRE' | 'AÑO') => void;
  clearError: () => void;
  reset: () => void;
  recalculateProfitabilityForExistingSales: () => Promise<void>;
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  kpis: null,
  analisisRentabilidad: null,
  alertas: null,
  isLoading: false,
  error: null,
  periodoSeleccionado: 'MES',

  fetchKPIs: async (periodo = 'MES') => {
    logger.debug('🚀 DASHBOARD: fetchKPIs called with periodo:', periodo);
    set({ isLoading: true, error: null });
    try {
      // Import Supabase client first to ensure it's initialized
      const { supabase } = await import('../lib/supabase');
      
      // Wait for session to be ready before making requests
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        logger.error('❌ Error getting session:', sessionError);
        throw new Error(`Error de autenticación: ${sessionError.message}`);
      }
      
      if (!session) {
        logger.warn('⚠️ DASHBOARD: No active session, waiting for authentication...');
        // Return empty KPIs and wait for session to be established
        const emptyKPIs: DashboardKPIs = {
          ventasTotales: 0,
          ventasHoy: 0,
          ventasSemana: 0,
          ventasMes: 0,
          clientesActivos: 0,
          productosVendidos: 0,
          rentabilidadPromedio: 0,
          productoMasVendido: 'N/A',
          clienteMasRentable: 'N/A',
          alertasChurn: [],
          inventarioBajo: [],
          clientesNuevos: 0,
          crecimientoVentas: 0,
          margenBrutoPromedio: 0,
          tasaRetencion: 0,
          rotacionInventario: 0,
          ticketPromedio: 0,
          tendenciaVentas: 'ESTABLE',
          metaMensual: 0,
          cumplimientoMeta: 0,
          facturasPagadas: 0,
          facturasPendientes: 0,
          facturasEnviadas: 0,
          facturasVencidas: 0,
          ventasAyer: 0,
          clientesActivosMesAnterior: 0,
          rentabilidadPromedioMesAnterior: 0,
          ticketPromedioMesAnterior: 0,
          ventasMesAnterior: 0,
          margenBrutoPromedioMesAnterior: 0,
          tasaRetencionMesAnterior: 0,
          facturasEntregadasNoPagadas: 0,
          montoFacturasEntregadasNoPagadas: 0,
          facturasCreadasNoEntregadas: 0,
          montoFacturasCreadasNoEntregadas: 0,
          totalNumeroVentas: 0,
          totalNumeroTransacciones: 0,
          valorTotalInventario: 0,
          pedidosActivos: 0,
        };
        
        set({ kpis: emptyKPIs, isLoading: false });
        return;
      }
      
      // Get current user from auth store
      const { useAuthStore } = await import('./useAuthStore');
      const authStore = useAuthStore.getState();
      const currentUser = authStore.usuario;
      
      const empresaId = getCurrentEmpresaIdSync();
      logger.debug('🔍 DASHBOARD: Current empresa ID:', empresaId);
      
      if (!currentUser || !empresaId || empresaId === 'sin-empresa') {
        logger.debug('⚠️ DASHBOARD: No empresa found, returning empty KPIs');
        // Return empty KPIs for new users
        const emptyKPIs: DashboardKPIs = {
          ventasTotales: 0,
          ventasHoy: 0,
          ventasSemana: 0,
          ventasMes: 0,
          clientesActivos: 0,
          productosVendidos: 0,
          rentabilidadPromedio: 0,
          productoMasVendido: 'N/A',
          clienteMasRentable: 'N/A',
          alertasChurn: [],
          inventarioBajo: [],
          clientesNuevos: 0,
          crecimientoVentas: 0,
          margenBrutoPromedio: 0,
          tasaRetencion: 0,
          rotacionInventario: 0,
          ticketPromedio: 0,
          tendenciaVentas: 'ESTABLE',
          metaMensual: 0,
          cumplimientoMeta: 0,
          facturasPagadas: 0,
          facturasPendientes: 0,
          facturasEnviadas: 0,
          facturasVencidas: 0,
          ventasAyer: 0,
          clientesActivosMesAnterior: 0,
          rentabilidadPromedioMesAnterior: 0,
          ticketPromedioMesAnterior: 0,
          ventasMesAnterior: 0,
          margenBrutoPromedioMesAnterior: 0,
          tasaRetencionMesAnterior: 0,
          facturasEntregadasNoPagadas: 0,
          montoFacturasEntregadasNoPagadas: 0,
          facturasCreadasNoEntregadas: 0,
          montoFacturasCreadasNoEntregadas: 0,
          totalNumeroVentas: 0,
          totalNumeroTransacciones: 0,
          valorTotalInventario: 0,
          ventasTrimestre: 0,
          ventasTrimestreAnterior: 0,
          ventasAno: 0,
          ventasAnoAnterior: 0,
          pedidosActivos: 0,
        };
        
        set({ kpis: emptyKPIs, isLoading: false });
        return;
      }
      
      logger.debug('📊 Fetching KPIs for empresa:', empresaId, 'periodo:', periodo);
      
      // Normalize to local date string (YYYY-MM-DD) for consistent timezone-safe comparison
      const toLocalDateString = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      
      // Calculate date ranges based on periodo
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const todayStr = toLocalDateString(today);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = toLocalDateString(yesterday);
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      // Quarter
      const quarterMonth = Math.floor(today.getMonth() / 3) * 3;
      const quarterStart = new Date(today.getFullYear(), quarterMonth, 1);
      const quarterEnd = new Date(today.getFullYear(), quarterMonth + 3, 0);
      const lastQuarterStart = new Date(today.getFullYear(), quarterMonth - 3, 1);
      const lastQuarterEnd = new Date(today.getFullYear(), quarterMonth, 0);
      // Year
      const yearStart = new Date(today.getFullYear(), 0, 1);
      const yearEnd = new Date(today.getFullYear(), 11, 31);
      const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
      const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
      
      // Fetch ventas data - ensure we have a valid session token
      const { data: ventas, error: ventasError } = await supabase
        .from('ventas')
        .select('*')
        .eq('empresa_id', empresaId);
        
      if (ventasError) {
        // Check if it's a network/fetch error
        if (ventasError.message?.includes('Failed to fetch') || ventasError.message?.includes('fetch')) {
          logger.error('❌ Network error fetching ventas - session may not be ready:', ventasError);
          throw new Error('Error de conexión. Por favor, recarga la página e intenta de nuevo.');
        }
        logger.error('❌ Error fetching ventas for KPIs:', ventasError);
        throw ventasError;
      }
      
      // Fetch facturas data
      const { data: facturas, error: facturasError } = await supabase
        .from('facturas')
        .select('*')
        .eq('empresa_id', empresaId);
        
      if (facturasError) {
        logger.error('❌ Error fetching facturas for KPIs:', facturasError);
        throw facturasError;
      }

      const { data: devoluciones, error: devolucionesError } = await supabase
        .from('factura_devoluciones')
        .select('factura_id, monto_devuelto')
        .eq('empresa_id', empresaId);

      if (devolucionesError) {
        logger.error('❌ Error fetching factura_devoluciones for KPIs:', devolucionesError);
        throw devolucionesError;
      }

      const devolucionPorFactura = new Map<string, number>();
      for (const d of devoluciones ?? []) {
        const fid = String(d.factura_id || '');
        if (!fid) continue;
        const monto = Number(d.monto_devuelto) || 0;
        devolucionPorFactura.set(fid, (devolucionPorFactura.get(fid) || 0) + monto);
      }
      
      // Fetch clientes data
      const { data: clientes, error: clientesError } = await supabase
        .from('clientes')
        .select('*')
        .eq('empresa_id', empresaId);
        
      if (clientesError) {
        logger.error('❌ Error fetching clientes for KPIs:', clientesError);
        throw clientesError;
      }
      
      // Fetch productos data for inventory calculation
      const { data: productos, error: productosError } = await supabase
        .from('productos')
        .select('*')
        .eq('empresa_id', empresaId);
        
      if (productosError) {
        logger.error('❌ Error fetching productos for KPIs:', productosError);
        throw productosError;
      }
      
      logger.debug('📊 Data fetched:', { ventas: ventas?.length, facturas: facturas?.length, clientes: clientes?.length });
      
      // Helper: get local date string from venta.fecha (can be ISO string or Date)
      const getVentaDateStr = (v: any) => toLocalDateString(new Date(v.fecha));
      /** Una factura válida en métricas de ventas (no anulada) — cada factura cuenta una sola vez. */
      const facturaMetricaOk = (f: any) => String(f.estado || '').toUpperCase() !== 'ANULADA';
      const getFacturaDateStr = (f: any) => toLocalDateString(new Date(f.fecha));
      const ingresoNetoFactura = (f: { id: string; total?: number; estado?: string }) => {
        if (!facturaMetricaOk(f)) return 0;
        const bruto = Number(f.total) || 0;
        const dev = devolucionPorFactura.get(f.id) || 0;
        return Math.max(0, bruto - dev);
      };
      
      // KPIs de ingresos desde facturas (libro de ventas), no desde la tabla ventas
      const ventasHoy = facturas?.filter(f => facturaMetricaOk(f) && getFacturaDateStr(f) === todayStr).reduce((sum, f) => sum + ingresoNetoFactura(f), 0) || 0;
      
      const ventasAyer = facturas?.filter(f => facturaMetricaOk(f) && getFacturaDateStr(f) === yesterdayStr).reduce((sum, f) => sum + ingresoNetoFactura(f), 0) || 0;
      
      const ventasSemana = facturas?.filter(f => {
        if (!facturaMetricaOk(f)) return false;
        const s = getFacturaDateStr(f);
        return s >= toLocalDateString(weekStart) && s <= todayStr;
      }).reduce((sum, f) => sum + ingresoNetoFactura(f), 0) || 0;
      
      const ventasMes = facturas?.filter(f => {
        if (!facturaMetricaOk(f)) return false;
        const s = getFacturaDateStr(f);
        return s >= toLocalDateString(monthStart) && s <= toLocalDateString(monthEnd);
      }).reduce((sum, f) => sum + ingresoNetoFactura(f), 0) || 0;
      
      const ventasMesAnterior = facturas?.filter(f => {
        if (!facturaMetricaOk(f)) return false;
        const s = getFacturaDateStr(f);
        return s >= toLocalDateString(lastMonthStart) && s <= toLocalDateString(lastMonthEnd);
      }).reduce((sum, f) => sum + ingresoNetoFactura(f), 0) || 0;
      
      const ventasTrimestre = facturas?.filter(f => {
        if (!facturaMetricaOk(f)) return false;
        const s = getFacturaDateStr(f);
        return s >= toLocalDateString(quarterStart) && s <= toLocalDateString(quarterEnd);
      }).reduce((sum, f) => sum + ingresoNetoFactura(f), 0) || 0;
      
      const ventasTrimestreAnterior = facturas?.filter(f => {
        if (!facturaMetricaOk(f)) return false;
        const s = getFacturaDateStr(f);
        return s >= toLocalDateString(lastQuarterStart) && s <= toLocalDateString(lastQuarterEnd);
      }).reduce((sum, f) => sum + ingresoNetoFactura(f), 0) || 0;
      
      const ventasAno = facturas?.filter(f => {
        if (!facturaMetricaOk(f)) return false;
        const s = getFacturaDateStr(f);
        return s >= toLocalDateString(yearStart) && s <= toLocalDateString(yearEnd);
      }).reduce((sum, f) => sum + ingresoNetoFactura(f), 0) || 0;
      
      const ventasAnoAnterior = facturas?.filter(f => {
        if (!facturaMetricaOk(f)) return false;
        const s = getFacturaDateStr(f);
        return s >= toLocalDateString(lastYearStart) && s <= toLocalDateString(lastYearEnd);
      }).reduce((sum, f) => sum + ingresoNetoFactura(f), 0) || 0;
      
      const ventasTotales = facturas?.filter(facturaMetricaOk).reduce((sum, f) => sum + ingresoNetoFactura(f), 0) || 0;
      
      // Retention: % of clients who bought last month and also bought this month (por factura)
      const ventasMesAnteriorList = facturas?.filter(f => {
        if (!facturaMetricaOk(f)) return false;
        const s = getFacturaDateStr(f);
        return s >= toLocalDateString(lastMonthStart) && s <= toLocalDateString(lastMonthEnd);
      }) || [];
      const ventasMesList = facturas?.filter(f => {
        if (!facturaMetricaOk(f)) return false;
        const s = getFacturaDateStr(f);
        return s >= toLocalDateString(monthStart) && s <= toLocalDateString(monthEnd);
      }) || [];
      const clientesQueCompraronMesAnterior = new Set(ventasMesAnteriorList.map((f: any) => f.cliente_id).filter(Boolean));
      const clientesQueCompraronEsteMes = new Set(ventasMesList.map((f: any) => f.cliente_id).filter(Boolean));
      const clientesRetenidos = new Set(Array.from(clientesQueCompraronMesAnterior).filter(id => clientesQueCompraronEsteMes.has(id)));
      const tasaRetencion = clientesQueCompraronMesAnterior.size > 0
        ? (clientesRetenidos.size / clientesQueCompraronMesAnterior.size) * 100
        : 0;
      // Same for previous period (for "vs mes anterior")
      const ventasDosMesesAtrasStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      const ventasDosMesesAtrasEnd = new Date(today.getFullYear(), today.getMonth() - 1, 0);
      const ventasDosMesesAtras = facturas?.filter(f => {
        if (!facturaMetricaOk(f)) return false;
        const s = getFacturaDateStr(f);
        return s >= toLocalDateString(ventasDosMesesAtrasStart) && s <= toLocalDateString(ventasDosMesesAtrasEnd);
      }) || [];
      const clientesQueCompraronDosMesesAtras = new Set(ventasDosMesesAtras.map((f: any) => f.cliente_id).filter(Boolean));
      const clientesRetenidosMesAnterior = new Set(Array.from(clientesQueCompraronDosMesesAtras).filter(id => clientesQueCompraronMesAnterior.has(id)));
      const tasaRetencionMesAnterior = clientesQueCompraronDosMesesAtras.size > 0
        ? (clientesRetenidosMesAnterior.size / clientesQueCompraronDosMesesAtras.size) * 100
        : 0;
      
      // Debug logs only in development
      logger.debug('📊 KPIs calculated:', { 
        ventasCount: ventas?.length,
        clientesActivos: clientes?.length || 0
      });
      
      const facturasPagadas =
        facturas?.filter((f) => facturaMetricaOk(f) && estadoPagoEsPagado(f.estado_pago)).length || 0;
      const facturasPendientes =
        facturas?.filter((f) => facturaMetricaOk(f) && estadoPagoRequiereCobro(f.estado_pago)).length || 0;
      
      const clientesActivos = clientes?.length || 0;
      const clientesActivosMesAnterior = clientes?.filter(c => {
        const clienteDate = new Date(c.created_at);
        return clienteDate >= lastMonthStart && clienteDate <= lastMonthEnd;
      }).length || 0;
      
      const facturasMesCount =
        facturas?.filter(f => {
          if (!facturaMetricaOk(f)) return false;
          const s = getFacturaDateStr(f);
          return s >= toLocalDateString(monthStart) && s <= toLocalDateString(monthEnd);
        }).length || 0;
      const ticketPromedio = facturasMesCount > 0 ? ventasMes / facturasMesCount : 0;
      const ventasMesAnteriorCount = ventasMesAnteriorList.length;
      const ticketPromedioMesAnterior = ventasMesAnteriorCount > 0 ? ventasMesAnterior / ventasMesAnteriorCount : 0;
      
      const crecimientoVentas = ventasMesAnterior > 0 ? ((ventasMes - ventasMesAnterior) / ventasMesAnterior) * 100 : 0;

      const parseFacturaItems = (raw: unknown): any[] => {
        if (raw == null || raw === '') return [];
        try {
          const v = typeof raw === 'string' ? JSON.parse(raw) : raw;
          return Array.isArray(v) ? v : [];
        } catch {
          return [];
        }
      };

      const productosById = new Map((productos || []).map((p: any) => [p.id, p]));

      let productoMasVendido = 'N/A';
      const unidadesPorProducto = new Map<string, { nombre: string; unidades: number }>();
      for (const f of facturas || []) {
        if (!facturaMetricaOk(f)) continue;
        const items = parseFacturaItems(f.items);
        for (const item of items) {
          const pid = item.productoId || item.producto_id;
          const nombreDesdeItem =
            typeof item.producto === 'string'
              ? item.producto
              : item.producto && typeof item.producto === 'object'
                ? (item.producto as { nombre?: string }).nombre
                : undefined;
          if (!pid && !nombreDesdeItem) continue;
          const key = String(pid || nombreDesdeItem);
          const nombre =
            (pid && productosById.get(pid)?.nombre) ||
            nombreDesdeItem ||
            'Producto';
          const existing = unidadesPorProducto.get(key);
          unidadesPorProducto.set(key, {
            nombre: existing?.nombre || nombre,
            unidades: (existing?.unidades || 0) + (item.cantidad || 1),
          });
        }
      }
      if (unidadesPorProducto.size > 0) {
        const top = Array.from(unidadesPorProducto.values()).sort((a, b) => b.unidades - a.unidades)[0];
        productoMasVendido = top?.nombre ?? 'N/A';
      }

      let clienteMasRentable = 'N/A';
      const gastoPorCliente = new Map<string, number>();
      for (const f of facturas || []) {
        if (!facturaMetricaOk(f) || !f.cliente_id) continue;
        const cid = f.cliente_id as string;
        gastoPorCliente.set(cid, (gastoPorCliente.get(cid) || 0) + (f.total || 0));
      }
      if (gastoPorCliente.size > 0 && clientes && clientes.length > 0) {
        const topClienteId = Array.from(gastoPorCliente.entries()).sort((a, b) => b[1] - a[1])[0]?.[0];
        const topCliente = clientes.find((c: any) => c.id === topClienteId);
        if (topCliente) {
          clienteMasRentable = `${topCliente.nombre} ${topCliente.apellido || ''}`.trim();
        }
      }

      // Facturas entregadas pero no pagadas
      const facturasEntregadasNoPagadasList = (facturas || []).filter((f: any) =>
        facturaMetricaOk(f) &&
        f.estado_entrega === 'ENTREGADO' &&
        estadoPagoRequiereCobro(f.estado_pago) &&
        f.estado !== 'ARCHIVADA' &&
        f.estado !== 'ELIMINADA'
      );
      const facturasEntregadasNoPagadas = facturasEntregadasNoPagadasList.length;
      const montoFacturasEntregadasNoPagadas = facturasEntregadasNoPagadasList
        .reduce((sum: number, f: any) => sum + (f.monto_pendiente ?? f.total ?? 0), 0);

      // Facturas creadas pero no entregadas
      const facturasCreadasNoEntregadasList = (facturas || []).filter((f: any) =>
        facturaMetricaOk(f) &&
        f.estado_entrega !== 'ENTREGADO' &&
        f.estado !== 'ARCHIVADA' &&
        f.estado !== 'ELIMINADA'
      );
      const facturasCreadasNoEntregadas = facturasCreadasNoEntregadasList.length;
      const montoFacturasCreadasNoEntregadas = facturasCreadasNoEntregadasList
        .reduce((sum: number, f: any) => sum + (f.total ?? 0), 0);

      // Rotación de inventario = unidades vendidas este mes / stock promedio actual
      const unidadesVendidasMes = (facturas || [])
        .filter((f: any) => {
          if (!facturaMetricaOk(f)) return false;
          const s = getFacturaDateStr(f);
          return s >= toLocalDateString(monthStart) && s <= toLocalDateString(monthEnd);
        })
        .reduce((sum: number, f: any) => {
          const items = parseFacturaItems(f.items);
          return sum + items.reduce((itemSum: number, item: any) => itemSum + (item.cantidad || 0), 0);
        }, 0);
      const stockPromedio = (productos || [])
        .filter((p: any) => String(p.estado || '').toUpperCase() !== 'ARCHIVADO' && !p.fecha_archivado)
        .filter((p: any) => p.inventory_type !== 'unlimited' && p.stock != null)
        .reduce((sum: number, p: any) => sum + (p.stock || 0), 0);
      const rotacionInventario = stockPromedio > 0
        ? parseFloat((unidadesVendidasMes / stockPromedio).toFixed(2))
        : 0;

      // Facturas vencidas (vencimiento pasada y pago pendiente)
      const todayDate = new Date();
      const facturasVencidas = (facturas || []).filter((f: any) => {
        if (!facturaMetricaOk(f)) return false;
        if (!f.vencimiento) return false;
        if (f.estado === 'ARCHIVADA' || f.estado === 'ELIMINADA') return false;
        if (f.estado_pago === 'PAGADO') return false;
        return new Date(f.vencimiento) < todayDate;
      }).length;

      const ventasMesAnteriorListVentas =
        ventas?.filter((v) => {
          const s = getVentaDateStr(v);
          return s >= toLocalDateString(lastMonthStart) && s <= toLocalDateString(lastMonthEnd);
        }) || [];

      const facturasNoAnuladasCount = facturas?.filter(facturaMetricaOk).length || 0;

      const pedidosActivos =
        facturas?.filter(
          (f: any) => facturaMetricaOk(f) && String(f.estado || '').toUpperCase() === 'ACTIVA'
        ).length || 0;

      const productosVendidosTotales =
        facturas?.filter(facturaMetricaOk).reduce((sum, f) => {
          const items = parseFacturaItems(f.items);
          return sum + items.reduce((itemSum: number, item: any) => itemSum + (item.cantidad || 0), 0);
        }, 0) || 0;

      const kpis: DashboardKPIs = {
        ventasTotales,
        ventasHoy,
        ventasSemana,
        ventasMes,
        clientesActivos,
        productosVendidos: productosVendidosTotales,
        rentabilidadPromedio: ventas?.length > 0 ? ventas.reduce((sum, v) => sum + (v.rentabilidad_real || 0), 0) / ventas.length : 0,
        productoMasVendido,
        clienteMasRentable,
        alertasChurn: [],
        inventarioBajo: [],
        clientesNuevos: clientesActivos - clientesActivosMesAnterior,
        crecimientoVentas,
        margenBrutoPromedio: (() => {
          if (!ventas || ventas.length === 0) return 0;
          
          // Calculate margin for each sale
          let totalMargin = 0;
          for (const v of ventas) {
            const profit = Number(v.rentabilidad_real || 0);
            const total = Number(v.total || 0);
            const margen = total > 0 ? (profit / total) * 100 : 0;
            totalMargin += margen;
          }
          
          return totalMargin / ventas.length;
        })(),
        rotacionInventario,
        ticketPromedio,
        tendenciaVentas: crecimientoVentas > 5 ? 'CRECIENTE' : crecimientoVentas < -5 ? 'DECRECIENTE' : 'ESTABLE',
        metaMensual: 0, // TODO: Set from configuration
        cumplimientoMeta: 0, // TODO: Calculate based on monthly goal
        facturasPagadas,
        facturasPendientes,
        facturasEnviadas: facturasNoAnuladasCount,
        facturasVencidas,
        ventasAyer,
        clientesActivosMesAnterior,
        rentabilidadPromedioMesAnterior:
          ventasMesAnteriorListVentas.length > 0
            ? ventasMesAnteriorListVentas.reduce((sum: number, v: any) => sum + (v.rentabilidad_real || 0), 0) /
              ventasMesAnteriorListVentas.length
            : 0,
        ticketPromedioMesAnterior,
        ventasMesAnterior,
        margenBrutoPromedioMesAnterior:
          ventasMesAnteriorListVentas.length > 0
            ? ventasMesAnteriorListVentas.reduce((sum: number, v: any) => {
                const margen = v.total > 0 ? ((v.rentabilidad_real || 0) / v.total) * 100 : 0;
                return sum + margen;
              }, 0) / ventasMesAnteriorListVentas.length
            : 0,
        tasaRetencion,
        tasaRetencionMesAnterior,
        facturasEntregadasNoPagadas,
        montoFacturasEntregadasNoPagadas,
        facturasCreadasNoEntregadas,
        montoFacturasCreadasNoEntregadas,
        totalNumeroVentas: facturasNoAnuladasCount,
        totalNumeroTransacciones: facturasNoAnuladasCount,
        valorTotalInventario: productos?.filter((p: any) => String(p.estado || '').toUpperCase() !== 'ARCHIVADO' && !p.fecha_archivado).reduce((sum, p) => sum + ((p.stock || 0) * (p.precio_compra || 0)), 0) || 0,
        ventasTrimestre,
        ventasTrimestreAnterior,
        ventasAno,
        ventasAnoAnterior,
        pedidosActivos,
      };
      
      set({ kpis, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error al cargar KPIs',
        isLoading: false
      });
    }
  },

  fetchAnalisisRentabilidad: async (_periodo = 'MES') => {
    set({ isLoading: true, error: null });
    try {
      const { supabase } = await import('../lib/supabase');
      const { useAuthStore } = await import('./useAuthStore');
      const authStore = useAuthStore.getState();
      const empresaId = getCurrentEmpresaIdSync();
      if (!authStore.usuario || !empresaId || empresaId === 'sin-empresa') {
        set({ analisisRentabilidad: emptyAnalisis(), isLoading: false });
        return;
      }

      const { data: ventas, error: ventasError } = await supabase
        .from('ventas')
        .select('id, fecha, total, rentabilidad_real, cliente_id, items')
        .eq('empresa_id', empresaId)
        .order('fecha', { ascending: true });

      if (ventasError) throw ventasError;

      const { data: clientes } = await supabase
        .from('clientes')
        .select('id, nombre, apellido')
        .eq('empresa_id', empresaId);

      const clientesMap = new Map((clientes || []).map((c: any) => [c.id, c]));

      const { data: productos } = await supabase
        .from('productos')
        .select('id, nombre')
        .eq('empresa_id', empresaId)
        .or('estado.eq.ACTIVO,estado.is.null')
        .neq('estado', 'ARCHIVADO');

      const now = new Date();
      const rentabilidadMensual = Array.from({ length: 6 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        const monthStr = d.toLocaleString('es-CO', { month: 'short', year: '2-digit' });
        const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
        const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        const ventasMes = (ventas || []).filter((v: any) => {
          const fecha = new Date(v.fecha);
          return fecha >= monthStart && fecha <= monthEnd;
        });
        const totalVentas = ventasMes.reduce((s: number, v: any) => s + (v.total || 0), 0);
        const totalRentabilidad = ventasMes.reduce((s: number, v: any) => s + (v.rentabilidad_real || 0), 0);
        const margenBruto = totalVentas > 0 ? totalRentabilidad / totalVentas : 0;
        return { fecha: monthStr, ventas: totalVentas, rentabilidad: totalRentabilidad, margenBruto };
      });

      const rentabilidadSemanal = Array.from({ length: 8 }, (_, i) => {
        const weekEnd = new Date(now);
        weekEnd.setDate(now.getDate() - i * 7);
        const weekStart = new Date(weekEnd);
        weekStart.setDate(weekEnd.getDate() - 6);
        const ventasSemana = (ventas || []).filter((v: any) => {
          const fecha = new Date(v.fecha);
          return fecha >= weekStart && fecha <= weekEnd;
        });
        const totalVentas = ventasSemana.reduce((s: number, v: any) => s + (v.total || 0), 0);
        const totalRentabilidad = ventasSemana.reduce((s: number, v: any) => s + (v.rentabilidad_real || 0), 0);
        const margenBruto = totalVentas > 0 ? totalRentabilidad / totalVentas : 0;
        return { fecha: `Sem ${8 - i}`, ventas: totalVentas, rentabilidad: totalRentabilidad, margenBruto };
      }).reverse();

      const rentabilidadPorCliente = new Map<string, number>();
      const ventasPorCliente = new Map<string, number>();
      const ultimaCompraPorCliente = new Map<string, Date>();
      for (const venta of ventas || []) {
        if (!venta.cliente_id) continue;
        rentabilidadPorCliente.set(venta.cliente_id, (rentabilidadPorCliente.get(venta.cliente_id) || 0) + (venta.rentabilidad_real || 0));
        ventasPorCliente.set(venta.cliente_id, (ventasPorCliente.get(venta.cliente_id) || 0) + 1);
        const fecha = new Date(venta.fecha);
        if (!ultimaCompraPorCliente.has(venta.cliente_id) || fecha > ultimaCompraPorCliente.get(venta.cliente_id)!) {
          ultimaCompraPorCliente.set(venta.cliente_id, fecha);
        }
      }
      const clientesConRentabilidad = Array.from(rentabilidadPorCliente.entries()).map(([id, rentabilidadTotal]) => {
        const cliente = clientesMap.get(id);
        const numVentas = ventasPorCliente.get(id) || 1;
        return {
          cliente: { id, nombre: cliente ? `${cliente.nombre} ${cliente.apellido || ''}`.trim() : 'Cliente' },
          rentabilidadTotal,
          rentabilidadPromedio: rentabilidadTotal / numVentas,
          frecuenciaCompra: numVentas,
          ultimaCompra: ultimaCompraPorCliente.get(id) || new Date(),
        };
      }).sort((a, b) => b.rentabilidadTotal - a.rentabilidadTotal);

      const rentabilidadPorProducto = new Map<string, { nombre: string; rentabilidad: number; unidades: number }>();
      for (const venta of ventas || []) {
        const items = venta.items ? (typeof venta.items === 'string' ? JSON.parse(venta.items) : venta.items) : [];
        for (const item of items) {
          const pid = item.productoId || item.producto_id;
          if (!pid) continue;
          const nombreProducto = (productos || []).find((p: any) => p.id === pid)?.nombre || item.producto || 'Producto';
          const ganancia = ((item.precioUnitario || 0) - (item.costoUnitario || 0)) * (item.cantidad || 1);
          const existing = rentabilidadPorProducto.get(pid) || { nombre: nombreProducto, rentabilidad: 0, unidades: 0 };
          rentabilidadPorProducto.set(pid, {
            nombre: nombreProducto,
            rentabilidad: existing.rentabilidad + ganancia,
            unidades: existing.unidades + (item.cantidad || 1),
          });
        }
      }
      const productosConRentabilidad = Array.from(rentabilidadPorProducto.entries())
        .map(([id, data]) => ({
          producto: { id, nombre: data.nombre },
          rentabilidadTotal: data.rentabilidad,
          unidadesVendidas: data.unidades,
          rotacion: data.unidades / 30,
        }))
        .sort((a, b) => b.rentabilidadTotal - a.rentabilidadTotal);

      const ingresosPorProducto = new Map<string, number>();
      for (const venta of ventas || []) {
        const items = venta.items ? (typeof venta.items === 'string' ? JSON.parse(venta.items) : venta.items) : [];
        for (const item of items) {
          const pid = item.productoId || item.producto_id;
          if (!pid) continue;
          ingresosPorProducto.set(pid, (ingresosPorProducto.get(pid) || 0) + (item.subtotal || 0));
        }
      }
      const productosOrdenados = Array.from(ingresosPorProducto.entries())
        .map(([id, ingresos]) => ({
          id,
          nombre: (productos || []).find((p: any) => p.id === id)?.nombre || 'Producto',
          ingresos,
        }))
        .sort((a, b) => b.ingresos - a.ingresos);
      const totalProductos = productosOrdenados.length;
      const claseACount = Math.ceil(totalProductos * 0.2);
      const claseBCount = Math.ceil(totalProductos * 0.3);
      const productosClaseA = productosOrdenados.slice(0, claseACount).map(p => p.nombre);
      const productosClaseB = productosOrdenados.slice(claseACount, claseACount + claseBCount).map(p => p.nombre);
      const productosClaseC = productosOrdenados.slice(claseACount + claseBCount).map(p => p.nombre);

      const rentabilidadTotal = (ventas || []).reduce((s: number, v: any) => s + (v.rentabilidad_real || 0), 0);
      const totalVentasAll = (ventas || []).reduce((s: number, v: any) => s + (v.total || 0), 0);
      const margenPromedio = totalVentasAll > 0 ? rentabilidadTotal / totalVentasAll : 0;

      const analisis: AnalisisRentabilidad = {
        rentabilidadMensual,
        rentabilidadSemanal,
        clientesMasRentables: clientesConRentabilidad.slice(0, 5),
        clientesMenosRentables: clientesConRentabilidad.slice(-5).reverse(),
        productosMasRentables: productosConRentabilidad.slice(0, 5),
        productosMenosRentables: productosConRentabilidad.slice(-5).reverse(),
        productosClaseA,
        productosClaseB,
        productosClaseC,
        rentabilidadTotal,
        margenPromedio,
      };

      set({ analisisRentabilidad: analisis, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error al cargar análisis de rentabilidad',
        isLoading: false,
      });
    }
  },

  fetchAlertas: async () => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { useAuthStore } = await import('./useAuthStore');
      const authStore = useAuthStore.getState();
      const empresaId = getCurrentEmpresaIdSync();
      if (!authStore.usuario || !empresaId || empresaId === 'sin-empresa') {
        set({ alertas: emptyAlertas(), isLoading: false });
        return;
      }

      const { data: productos } = await supabase
        .from('productos')
        .select('id, nombre, sku, stock, stock_minimo, precio, inventory_type, estado, fecha_archivado')
        .eq('empresa_id', empresaId)
        .or('estado.eq.ACTIVO,estado.is.null')
        .neq('estado', 'ARCHIVADO')
        .is('fecha_archivado', null);

      const stockBajo = (productos || []).filter((p: any) => {
        if (String(p.estado || '').toUpperCase() === 'ARCHIVADO' || p.fecha_archivado) return false;
        if (p.inventory_type === 'unlimited' || p.stock === null || p.stock === undefined) return false;
        const minimo = p.stock_minimo ?? 0;
        if (Number(minimo) <= 0) return false;
        return p.stock <= minimo;
      }).map((p: any) => ({
        id: p.id,
        nombre: p.nombre,
        sku: p.sku || '',
        stock: p.stock || 0,
        stockMinimo: p.stock_minimo ?? 0,
        precioVenta: p.precio ?? 0,
      }));

      const { data: clientes } = await supabase
        .from('clientes')
        .select('id, nombre, apellido, email, ultima_compra, created_at')
        .eq('empresa_id', empresaId)
        .eq('activo', true);

      const hace30Dias = new Date();
      hace30Dias.setDate(hace30Dias.getDate() - 30);

      const clientesSinComprar = (clientes || [])
        .filter((c: any) => {
          const ultimaCompra = c.ultima_compra ? new Date(c.ultima_compra) : new Date(c.created_at);
          return ultimaCompra < hace30Dias;
        })
        .map((c: any) => ({
          id: c.id,
          nombre: `${c.nombre} ${c.apellido || ''}`.trim(),
          email: c.email || '',
          ultimaCompra: c.ultima_compra ? new Date(c.ultima_compra) : new Date(c.created_at),
          riesgoChurn: (() => {
            const dias = Math.floor((Date.now() - new Date(c.ultima_compra || c.created_at).getTime()) / (1000 * 60 * 60 * 24));
            if (dias > 90) return 'ALTO';
            if (dias > 60) return 'MEDIO';
            return 'BAJO';
          })(),
        }));

      const alertas: SistemaAlertas = {
        alertasInventario: stockBajo.length,
        alertasChurn: clientesSinComprar.filter((c: any) => c.riesgoChurn === 'ALTO').length,
        alertasRentabilidad: 0,
        alertasPendientes: stockBajo.length + clientesSinComprar.filter((c: any) => c.riesgoChurn === 'ALTO').length,
        stockBajo,
        clientesSinComprarEnDias: clientesSinComprar,
      };

      set({ alertas });
    } catch {
      set({ alertas: emptyAlertas() });
    }
  },

  setPeriodoSeleccionado: (periodo) => {
    set({ periodoSeleccionado: periodo });
    // Recargar datos cuando cambie el período
    get().fetchKPIs(periodo);
    get().fetchAnalisisRentabilidad(periodo);
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      kpis: null,
      analisisRentabilidad: null,
      alertas: null,
      isLoading: false,
      error: null,
    });
  },

  // Function to recalculate and update profitability for existing sales
  recalculateProfitabilityForExistingSales: async () => {
    logger.debug('🔄 Recalculating profitability for existing sales...');
    try {
      const empresaId = getCurrentEmpresaIdSync();
      if (!empresaId || empresaId === 'sin-empresa') {
        throw new Error('No empresa ID available');
      }

      const { supabase } = await import('../lib/supabase');
      
      // Get all ventas to check what columns exist
      const { data: ventas, error } = await supabase
        .from('ventas')
        .select('*')
        .eq('empresa_id', empresaId);

      if (error) {
        logger.error('❌ Error fetching ventas:', error);
        throw error;
      }

      logger.debug(`📊 Found ${ventas?.length || 0} ventas to recalculate`);
      
      // Debug: Check what columns exist in the ventas table
      if (ventas && ventas.length > 0) {
        logger.debug('🔍 Sample venta columns:', Object.keys(ventas[0]));
        logger.debug('🔍 Sample venta data:', ventas[0]);
        
        // Check if rentabilidad_real or rentabilidad column exists
        const hasRentabilidadReal = 'rentabilidad_real' in ventas[0];
        const hasRentabilidad = 'rentabilidad' in ventas[0];
        logger.debug('🔍 Has rentabilidad_real column?', hasRentabilidadReal);
        logger.debug('🔍 Has rentabilidad column?', hasRentabilidad);
      }

      for (const venta of ventas || []) {
        try {
          // Parse items from JSON
          const items = venta.items ? (typeof venta.items === 'string' ? JSON.parse(venta.items) : venta.items) : [];
          
          // Calculate profitability: (sale price - cost) * quantity for each item
          let rentabilidadReal = 0;
          let costoTotal = 0;
          let ventaTotal = 0;

          items.forEach((item: any) => {
            const costoItem = (item.costoUnitario || 0) * item.cantidad;
            const ventaItem = item.total || (item.precioUnitario * item.cantidad);
            
            costoTotal += costoItem;
            ventaTotal += ventaItem;
          });

          rentabilidadReal = ventaTotal - costoTotal;

          logger.debug(`📊 Venta ${venta.numero}: Total=${ventaTotal}, Cost=${costoTotal}, Profit=${rentabilidadReal}`);

          // Update the venta in Supabase
          const { error: updateError } = await supabase
            .from('ventas')
            .update({ rentabilidad_real: rentabilidadReal })
            .eq('id', venta.id);

          if (updateError) {
            logger.error(`❌ Error updating venta ${venta.id}:`, updateError);
          } else {
            logger.debug(`✅ Updated venta ${venta.numero} with rentabilidad_real: ${rentabilidadReal}`);
          }
        } catch (itemError) {
          logger.error(`❌ Error processing venta ${venta.id}:`, itemError);
        }
      }

      logger.debug('✅ Finished recalculating profitability for existing sales');
    } catch (error) {
      logger.error('❌ Error in recalculateProfitabilityForExistingSales:', error);
      throw error;
    }
  }
}));

